import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import SyncIndicator from "./SyncIndicator";
import {
  LayoutGrid, Route as RouteIcon, CalendarDays, Wrench, Fuel, ShieldAlert, BellRing,
  BarChart3, Trophy, AlertTriangle, IndianRupee, Settings, LogOut, QrCode, ArrowRightLeft, FileCheck2, CarFront, MoreHorizontal, History,
  Table2,
} from "lucide-react";
import { InstallButton } from "./InstallApp";

const NAV = {
  sales: [["/board", "Board", LayoutGrid], ["/trips", "Trips", RouteIcon], ["/damage", "Damage", ShieldAlert], ["/notifications", "Alerts", BellRing]],
  sm: [["/board", "Board", LayoutGrid], ["/approvals", "Approvals", FileCheck2], ["/trips", "Trips", RouteIcon], ["/dashboard", "Dashboard", BarChart3], ["/notifications", "Alerts", BellRing]],
  tdc: [["/board", "Board", LayoutGrid], ["/calendar", "Calendar", CalendarDays], ["/workshop", "Workshop", Wrench], ["/fuel", "Fuel", Fuel], ["/notifications", "Alerts", BellRing]],
  security: [["/scan", "Scan", QrCode], ["/scan/out", "Out Now", CarFront], ["/scan/history", "History", History]],
  vp: [["/dashboard", "Dashboard", BarChart3], ["/td-summary", "TD Summary", Table2], ["/approvals", "Approvals", FileCheck2], ["/board", "Board", LayoutGrid], ["/notifications", "Alerts", BellRing]],
  zh: [["/dashboard", "Dashboard", BarChart3], ["/board", "Board", LayoutGrid], ["/transfers", "Transfers", ArrowRightLeft], ["/exceptions", "Exceptions", AlertTriangle], ["/notifications", "Alerts", BellRing]],
  md: [["/md", "MD View", BarChart3], ["/td-summary", "TD Summary", Table2], ["/board", "Board", LayoutGrid], ["/leaderboard", "KECs", Trophy], ["/notifications", "Alerts", BellRing]],
  aho: [["/accounts", "Accounts", IndianRupee], ["/board", "Fleet", LayoutGrid], ["/notifications", "Alerts", BellRing]],
  azh: [["/accounts", "Accounts", IndianRupee], ["/board", "Fleet", LayoutGrid], ["/notifications", "Alerts", BellRing]],
  admin: [["/board", "Board", LayoutGrid], ["/fleet-master", "Vehicles", CarFront], ["/td-summary", "TD Summary", Table2], ["/admin", "Admin", Settings], ["/notifications", "Alerts", BellRing]],
};

const EXTRA = {
  sales: [["/compliance", "Compliance"], ["/leaderboard", "KEC Leaderboard"], ["/transfers", "Transfers"]],
  sm: [["/calendar", "TD Calendar"], ["/transfers", "Transfers"], ["/workshop", "Workshop"], ["/damage", "Damage"], ["/compliance", "Compliance"], ["/exceptions", "Exceptions"], ["/leaderboard", "KEC Leaderboard"], ["/admin", "KEC Team"]],
  tdc: [["/board", "Board"], ["/trips", "Trips"], ["/fleet-master", "Vehicle Master"], ["/transfers", "Transfers"], ["/damage", "Damage"], ["/compliance", "Compliance"]],
  vp: [["/transfers", "Transfers"], ["/md", "Group Overview"], ["/exceptions", "Exceptions"], ["/leaderboard", "KEC Leaderboard"], ["/accounts", "Accounts"], ["/workshop", "Workshop"], ["/compliance", "Compliance"], ["/admin", "Configuration"]],
  zh: [["/td-summary", "TD Summary"], ["/leaderboard", "KEC Leaderboard"], ["/accounts", "Accounts"], ["/workshop", "Workshop"], ["/compliance", "Compliance"]],
  md: [["/transfers", "Transfers"], ["/dashboard", "Utilization Detail"], ["/accounts", "Accounts"], ["/exceptions", "Exceptions"], ["/compliance", "Compliance"]],
  admin: [["/dashboard", "Dashboard"], ["/transfers", "Transfers"], ["/exceptions", "Exceptions"], ["/scan", "Gate Scan (Admin)"], ["/scan/out", "Vehicles Out Now"], ["/scan/history", "Gate History"], ["/accounts", "Accounts"], ["/compliance", "Compliance"], ["/workshop", "Workshop"]],
  aho: [["/exceptions", "Exceptions"]],
  azh: [["/exceptions", "Exceptions"]],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [more, setMore] = useState(false);
  const nav = NAV[user.role] || NAV.sales;
  const extra = EXTRA[user.role] || [];
  const roleTag = user.role === "sales" ? "KEC" : user.role;

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f6f9] lg:flex">
      <aside className="hidden lg:flex lg:w-60 lg:flex-col lg:fixed lg:inset-y-0 bg-[#0E2240] text-white">
        <div className="px-5 py-6">
          <div className="font-heading text-xl font-800 font-bold tracking-tight">TDFMS</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Incheon Motors · Kia</div>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {nav.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/scan"} data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${isActive ? "bg-[#BB162B] text-white" : "text-slate-300 hover:bg-white/10"}`}>
              <Icon size={17} /> {label}
              {label === "Alerts" && unread > 0 && <span className="ml-auto rounded-full bg-[#BF9B30] px-1.5 text-[10px] font-bold text-[#0E2240]">{unread}</span>}
            </NavLink>
          ))}
          {extra.length > 0 && <div className="pt-3 pb-1 px-3 text-[10px] uppercase tracking-widest text-slate-500">More</div>}
          {extra.map(([to, label]) => (
            <NavLink key={to + label} to={to} className={({ isActive }) => `block rounded-lg px-3 py-2 text-[13px] ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>{label}</NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold">{user.name}</div>
            <SyncIndicator />
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#BF9B30]">{roleTag}</div>
          <button data-testid="logout-btn" onClick={logout} className="mt-2 flex items-center gap-2 text-xs text-slate-400 hover:text-white"><LogOut size={13} /> Sign out</button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-[#0E2240] px-4 py-3 text-white">
          <div>
            <span className="font-heading text-lg font-bold">TDFMS</span>
            <span className="ml-2 text-[10px] uppercase tracking-wider text-[#BF9B30]">{roleTag}</span>
          </div>
          <div className="flex items-center gap-3">
            <SyncIndicator />
            <button data-testid="logout-btn-mobile" onClick={logout} className="text-slate-300"><LogOut size={18} /></button>
          </div>
        </header>
        <main className="flex-1 pb-20 lg:pb-6">{children}</main>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 flex justify-around border-t border-slate-200 bg-white py-1.5 shadow-[0_-2px_10px_rgba(14,34,64,0.08)]">
          {nav.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/scan"} data-testid={`bottomnav-${label.toLowerCase()}`} onClick={() => setMore(false)}
              className={({ isActive }) => `relative flex flex-col items-center gap-0.5 px-2.5 py-1 text-[10px] font-semibold ${isActive ? "text-[#BB162B]" : "text-slate-400"}`}>
              <Icon size={20} /> {label}
              {label === "Alerts" && unread > 0 && <span className="absolute -top-0.5 right-1 h-2 w-2 rounded-full bg-[#BB162B]" />}
            </NavLink>
          ))}
          {extra.length > 0 && (
            <button data-testid="bottomnav-more" onClick={() => setMore(!more)}
              className={`flex flex-col items-center gap-0.5 px-2.5 py-1 text-[10px] font-semibold ${more ? "text-[#BB162B]" : "text-slate-400"}`}>
              <MoreHorizontal size={20} /> More
            </button>
          )}
        </nav>
        {more && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMore(false)}>
            <div data-testid="more-sheet" className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white p-4 pb-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-200" />
              <div className="px-1 text-[10px] uppercase tracking-widest text-slate-400">More</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {extra.map(([to, label]) => (
                  <NavLink key={to + label} to={to} onClick={() => setMore(false)} data-testid={`more-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) => `rounded-xl px-3 py-3 text-sm font-semibold ${isActive ? "bg-[#0E2240] text-white" : "bg-slate-50 text-slate-700"}`}>{label}</NavLink>
                ))}
              </div>
              <div className="mt-3"><InstallButton compact className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#BB162B]/10 px-3 py-3 text-sm font-bold text-[#BB162B]" /></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
